import {I18n, Translate} from 'react-redux-i18n';
import PropTypes from 'prop-types';
import {AbstractFormComponent, Icon, InfoAdornment, TextPhoneNumber} from 'front-onceforall-core';
import {requiredValidator, emailValidator, phoneNumberValidator, isIdentificationNumberValid} from 'front-onceforall-core/dist/utils/validators';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {isEmpty} from 'front-onceforall-core/dist/utils/strings';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';
import {getVATIdentificationNumberFromOrganisation, VAT_IDENTIFICATION_NUMBER_CODE, REGISTRATION_NUMBER_CODE} from 'front-onceforall-core/dist/utils/organisations';
import CountrySelector from '../../countrySelector';
import {getPhoneLocaleFromCountry} from '../../../utils/country';
import * as snackbarsActions from '../../../actions/snackbars';
import organisationService from '../../../services/organisation/OrganisationService';
import identificationNumberService from '../../../services/identificationNumber/IdentificationNumberService';
import {availableLanguages} from '../../../translations';
import {captureError} from '../../../utils/log';
import Message from './message';
import messageTypes from './utils/messageTypes';
import organisationStatusType from './utils/organisationStatusType';
import {errorCode} from '../../../utils/constant';

/**                         invited
 *                | FR     | DE   | IT              | Other |
 *                -------------------------------------------
 *             FR | Siret* | VAT* | VAT             | VAT*  |
 * inviting    DE | VAT*   | VAT  | VAT             | VAT*  |
               IT | VAT*   | VAT* | Codice Fiscale* | VAT*  |
 *          Other | VAT*   | VAT* | VAT             | VAT*  |
 */
export class ExternalInvitationForm extends AbstractFormComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      defaultPhoneCountry: getPhoneLocaleFromCountry(props.defaultCountry),
      organisationStatus: organisationStatusType.VALID,
      organisationAddress: null,
      alreadyExists: null
    };

    this.areInvitingAndInvitedOrganisationsBothInSameCountry = this.areInvitingAndInvitedOrganisationsBothInSameCountry.bind(this);
    this.changeCustomMessage = this.changeCustomMessage.bind(this);
    this.renderIdentificationNumberInput = this.renderIdentificationNumberInput.bind(this);
    this.fetchOrganisationInformation = this.fetchOrganisationInformation.bind(this);
  }

  getFormData() {
    const {defaultOrganisation} = this.props;
    if (defaultOrganisation) {
      return {
        invitingCountry: this.props.invitingOrganisationCountry,
        organisationName: defaultOrganisation.fullName,
        email: '',
        firstName: '',
        lastName: '',
        language: this.props.currentLocale,
        customMessage: '',
        phoneNumber: '',
        registrationNumber: defaultOrganisation.registrationNumber,
        vatIdentificationNumber: getVATIdentificationNumberFromOrganisation(defaultOrganisation),
        country: defaultOrganisation.country
      };
    }

    return {
      invitingCountry: this.props.invitingOrganisationCountry,
      organisationName: this.props.defaultValue,
      email: '',
      firstName: '',
      lastName: '',
      language: this.props.currentLocale,
      customMessage: '',
      phoneNumber: '',
      registrationNumber: '',
      vatIdentificationNumber: '',
      country: this.props.defaultCountry
    };
  }

  getFormValidators() {
    return {
      organisationName: requiredValidator,
      email: [requiredValidator, emailValidator],
      firstName: requiredValidator,
      lastName: requiredValidator,
      language: requiredValidator,
      customMessage: requiredValidator,
      phoneNumber: [requiredValidator, phoneNumberValidator],
      registrationNumber: [
        this.nonEmptyRegistrationNumberValidator.bind(this),
        this.registrationNumberFormatValidator.bind(this),
        this.organisationActiveInILGValidator.bind(this)
      ],
      vatIdentificationNumber: [
        this.nonEmptyVatIdentificationNumberValidator.bind(this),
        this.vatIdentificationNumberFormatValidator.bind(this)
      ],
      country: requiredValidator
    };
  }

  nonEmptyRegistrationNumberValidator(value) {
    if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.ITALY])) {
      return requiredValidator(value);
    }

    return null;
  }

  registrationNumberFormatValidator(value) {
    if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.ITALY])) {
      return this.isRegistrationNumberValid(value) ? null : 'error.invalidRegistrationNumber';
    }

    return null;
  }

  organisationActiveInILGValidator() {
    if (this.state.organisationStatus === organisationStatusType.INVALID) {
      return 'organisation.inactive';
    }

    return null;
  }

  nonEmptyVatIdentificationNumberValidator(value) {
    if (!this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.GERMANY, COUNTRY.ITALY])) {
      return requiredValidator(value);
    }

    return null;
  }

  vatIdentificationNumberFormatValidator(value) {
    if (!this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.GERMANY, COUNTRY.ITALY])) {
      return this.isVatIdentificationNumberValid(value) ? null : 'error.invalidIdentificationNumber';
    }

    return null;
  }

  componentDidMount() {
    this.changeCustomMessage(this.props.currentLocale);
    Promise.all([
      this.fetchIdentificationNumberTypes(this.props.defaultCountry),
      this.fetchRegistrationNumberTypes()
    ])
      .then(() => {
        // If defautValue may be the registration number / identification number
        // prefill the correct field, otherwise prefill the organisation name
        if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.ITALY])) {
          if (!this.props.defaultOrganisation && this.isRegistrationNumberValid(this.props.defaultValue)) {
            const {formData} = this.state;
            formData.organisationName = '';
            formData.registrationNumber = this.props.defaultValue;
            this.setState({formData});
          }

          this.fetchOrganisationInformation('registrationNumber', this.state.formData.registrationNumber);
        } else if (!this.props.defaultOrganisation && this.isVatIdentificationNumberValid(this.props.defaultValue)) {
          const {formData} = this.state;
          formData.organisationName = '';
          formData.vatIdentificationNumber = this.props.defaultValue;
          this.setState({formData});
          this.fetchOrganisationInformation('vatIdentificationNumber', this.state.formData.vatIdentificationNumber);
        }
      })
      .then(() => {
        this.setState({loading: false});
      })
      .catch(error => {
        captureError('Error while fetching types in external invitation', error);
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  areInvitingAndInvitedOrganisationsBothInSameCountry(countries) {
    return countries.includes(this.state.formData.country) &&
      (this.state.formData.country === this.state.formData.invitingCountry);
  }

  isRegistrationNumberValid(value, inProgress = false) {
    return isIdentificationNumberValid(this.state.registrationNumberTypes[this.state.formData.country], value, inProgress);
  }

  isVatIdentificationNumberValid(value, inProgress = false) {
    return isIdentificationNumberValid(this.state.identificationNumberTypes.find(type => type.code === VAT_IDENTIFICATION_NUMBER_CODE), value, inProgress);
  }

  isVatNumberFormatEqualToRegNumberFormat = () => {
    const regNumberFormat = this.state.identificationNumberTypes.find(type => type.code === REGISTRATION_NUMBER_CODE).format;
    const vatNumberFormat = this.state.identificationNumberTypes.find(type => type.code === VAT_IDENTIFICATION_NUMBER_CODE).format;
    return regNumberFormat === vatNumberFormat;
  };

  handleRegistrationNumberChange = event => {
    const {value} = event.target;
    if (!this.isRegistrationNumberValid(value, true)) {
      return;
    }

    this.handleChange('registrationNumber')(event);
  };

  handleVatIdentificationNumberChange = event => {
    const {value} = event.target;
    if (!this.isVatIdentificationNumberValid(value, true)) {
      return;
    }

    this.handleChange('vatIdentificationNumber')(event);
  };

  handleChangeCallback(name, value) {
    if (name === 'language') {
      this.changeCustomMessage(value);
      return;
    }

    if (name === 'country') {
      this.fetchIdentificationNumberTypes(value)
        .then(() => {
          const formData = {
            ...this.state.formData,
            country: value,
            registrationNumber: '',
            vatIdentificationNumber: ''
          };
          this.setState({
            formData,
            defaultPhoneCountry: getPhoneLocaleFromCountry(value),
            organisationStatus: organisationStatusType.VALID,
            organisationAddress: null,
            alreadyExists: null,
            alreadyInvited: false
          }, () => {
            this.validateField('registrationNumber', formData.registrationNumber);
            this.validateField('vatIdentificationNumber', formData.vatIdentificationNumber);
          });
        });
      return;
    }

    if (name === 'registrationNumber' || name === 'vatIdentificationNumber') {
      this.setState({
        alreadyInvited: false,
        alreadyExists: null,
        organisationStatus: organisationStatusType.VALID,
        organisationAddress: null
      });

      this.fetchOrganisationInformation(name, value);
    }
  }

  changeCustomMessage(language) {
    const {formData} = this.state;
    formData.customMessage = this.props.translateInvitationMessage(language);

    this.setState({
      formData
    });
  }

  fetchOrganisationInformation(regType, registrationNumber) {
    if (
      !isEmpty(registrationNumber) &&
      (
        (regType === 'registrationNumber' && this.registrationNumberFormatValidator(registrationNumber) === null) ||
        (regType === 'vatIdentificationNumber' && this.isVatNumberFormatEqualToRegNumberFormat() && this.isVatIdentificationNumberValid(registrationNumber))
      )
    ) {
      this.setState({loading: true});
      organisationService.getByRegistrationNumberAndCountry(registrationNumber, this.state.formData.country, true)
        .then(foundOrganisation => {
          const {formData} = this.state;
          let organisationAddress = null;
          let organisationStatus = organisationStatusType.VALID;
          let alreadyExists = null;
          if (foundOrganisation) {
            formData.organisationName = foundOrganisation.fullName;
            organisationAddress = foundOrganisation.address;
            organisationStatus = foundOrganisation.status ? organisationStatusType.VALID : organisationStatusType.INVALID;
            alreadyExists = (!foundOrganisation.remote && foundOrganisation.depositor) ? foundOrganisation : null;
          } else if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE])) {
            organisationStatus = organisationStatusType.UNKNOWN;
          }

          this.setState({formData, organisationAddress, loading: false, organisationStatus, alreadyExists}, () => {
            this.validateField('registrationNumber', formData.registrationNumber);
          });
        })
        .catch(error => {
          captureError('Error while searching organisation for external invitation', error);
          this.props.actions.addError(<Translate value="error.generic"/>);
        });
    }
  }

  fetchRegistrationNumberTypes() {
    return identificationNumberService.findAllRegistrationNumberType()
      .then(types => {
        this.setState({
          registrationNumberTypes: types.reduce((map, idNumber) => ({...map, [idNumber.country]: idNumber}), {})
        });
      });
  }

  fetchIdentificationNumberTypes(country) {
    return identificationNumberService.findAllByCountry(country)
      .then(identificationNumberTypes => {
        this.setState({identificationNumberTypes});
      });
  }

  handleSubmitCallback() {
    const formData = {
      ...this.state.formData,
      customMessage: this.state.formData.customMessage.replace(/\n/g, ' ')
    };

    if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.ITALY])) {
      formData.vatIdentificationNumber = null;
    } else {
      formData.registrationNumber = null;
    }

    this.props.onValidate(formData)
      .then(response => {
        if (response === errorCode.SAME_INVITATION_ALREADY_PENDING) {
          this.setState({
            alreadyInvited: true,
            loading: false,
            submitting: false
          });
        }
      });
  }

  renderIdentificationNumberInput() {
    if (this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.FRANCE, COUNTRY.ITALY])) {
      return (
        <TextField
          data-id="registrationNumber"
          label={this.getFieldLabel(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.name`, true)}
          value={this.state.formData.registrationNumber}
          onChange={this.handleRegistrationNumberChange}
          error={this.isOnError('registrationNumber')}
          helperText={this.helperText('registrationNumber')}
          disabled={this.props.disableOrganisationFields}
          InputProps={{
            endAdornment: (
              <InfoAdornment text={I18n.t(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.tooltip`)}/>
            )
          }}
        />);
    }

    return (
      <TextField
        data-id="vatIdentificationNumber"
        label={this.getFieldLabel(`identificationNumber.${this.state.formData.country}.${VAT_IDENTIFICATION_NUMBER_CODE}.name`, !this.areInvitingAndInvitedOrganisationsBothInSameCountry([COUNTRY.GERMANY]))}
        value={this.state.formData.vatIdentificationNumber}
        onChange={this.handleVatIdentificationNumberChange}
        error={this.isOnError('vatIdentificationNumber')}
        helperText={this.helperText('vatIdentificationNumber')}
        disabled={this.props.disableOrganisationFields}
        InputProps={{
          endAdornment: (
            <InfoAdornment
              text={I18n.t(
                `identificationNumber.${this.state.formData.country}.${VAT_IDENTIFICATION_NUMBER_CODE}.tooltip`
              )}
            />
          )
        }}
      />);
  }

  render() {
    if (!this.state.registrationNumberTypes) {
      return null;
    }

    const {organisationAddress} = this.state;
    const invalidOrganisation =
      // Boolean(this.state.alreadyExists) || /* REMOVED TEMPORARILY FOR PLATINT-5968 */
      this.state.organisationStatus === organisationStatusType.INVALID;

    return (
      <form
        onSubmit={this.handleSubmit}
        className="p-0"
      >
        {this.state.loading && <div className="loading-overlay"/>}
        {this.props.title &&
        <h2>
          <Translate value={`${this.props.title}.invite.title`}/>
        </h2>
        }

        <h4>
          <Translate value="invite.organisationSectionTitle"/>
        </h4>
        <div className="row">
          <div className="col-md-4">
            <CountrySelector
              onChange={this.handleChange('country')}
              value={this.state.formData.country}
              countriesToShow={Object.keys(this.state.registrationNumberTypes)}
              disabledCountrySelector={this.props.disableOrganisationFields}
            />
          </div>
          <div className="col-md-8">
            {this.renderIdentificationNumberInput()}
          </div>
        </div>

        <TextField
          data-id="organisationName"
          label={this.getFieldLabel('invite.organisationName', true)}
          value={this.state.formData.organisationName}
          onChange={this.handleChange('organisationName')}
          error={this.isOnError('organisationName')}
          helperText={this.helperText('organisationName')}
          disabled={this.props.disableOrganisationFields}
        />

        {organisationAddress &&
          <div className="box info information-primary font-size-xs p-3 flex-container">
            <div>
              <Translate
                value="address.title"
                tag="span"
              />:
            </div>
            <div>{organisationAddress.addressLine}, {organisationAddress.postCode} {organisationAddress.city}</div>
          </div>
        }

        {this.state.organisationStatus === organisationStatusType.INVALID &&
          <Message
            data-id="inactive-message-box"
            type={messageTypes.ERROR}
            title={<Translate value="invite.inactive.title"/>}
            text={<Translate value="invite.inactive.text"/>}
            icon="rounded-cross"
          />
        }

        {!this.state.alreadyInvited && this.state.organisationStatus === organisationStatusType.UNKNOWN &&
          <Message
            data-id="unknown-message-box"
            type={messageTypes.WARNING}
            title={<Translate value="invite.unknown.title"/>}
            text={<Translate value="invite.unknown.text"/>}
            icon="warning"
          />
        }

        {this.state.alreadyInvited &&
          <Message
            data-id="already-invited-message-box"
            type={messageTypes.ERROR}
            title={<Translate value="invite.alreadyInvited.title"/>}
            text={<Translate value="invite.alreadyInvited.text"/>}
            icon="rounded-cross"
          />
        }

        {this.state.alreadyExists &&
          <Message
            data-id="already-exists-message-box"
            type={messageTypes.INFO}
            title={<Translate value="invite.alreadyExists.title"/>}
            text={<Translate value="invite.alreadyExists.text"/>}
          >
            <button
              className="small primary"
              onClick={() => this.props.onInviteInternalOrganisation(this.state.alreadyExists)}
            >
              <Translate value="invite.alreadyExists.action"/>
            </button>
          </Message>
        }

        <h4>
          <Translate value="invite.userSectionTitle"/>
        </h4>
        <TextField
          label={this.getFieldLabel('invite.firstName', true)}
          value={this.state.formData.firstName}
          onChange={this.handleChange('firstName')}
          error={this.isOnError('firstName')}
          helperText={this.helperText('firstName')}
          disabled={invalidOrganisation}
        />
        <TextField
          label={this.getFieldLabel('invite.lastName', true)}
          value={this.state.formData.lastName}
          onChange={this.handleChange('lastName')}
          error={this.isOnError('lastName')}
          helperText={this.helperText('lastName')}
          disabled={invalidOrganisation}
        />
        <div className="row">
          <div className="col-lg-4">
            <TextPhoneNumber
              label={this.getFieldLabel('invite.phoneNumber', true)}
              value={this.state.formData.phoneNumber}
              onChange={this.handleChange('phoneNumber')}
              error={this.isOnError('phoneNumber')}
              helperText={this.helperText('phoneNumber')}
              defaultLocal={this.state.defaultPhoneCountry}
              currentLocale={this.props.currentLocale}
              disabled={invalidOrganisation}
            />
          </div>
          <div className="col-lg-8">
            <TextField
              label={this.getFieldLabel('invite.email', true)}
              value={this.state.formData.email}
              onChange={this.handleChange('email')}
              error={this.isOnError('email')}
              helperText={this.helperText('email')}
              disabled={invalidOrganisation}
            />
          </div>
        </div>

        <h4>
          <Translate value="invite.messageSectionTitle"/>
        </h4>
        <div className="font-size-s mt-2 mb-4">
          <Translate value="invite.messageSectionSubtitle"/>
        </div>
        <TextField
          select
          label={this.getFieldLabel('invite.language', true)}
          value={this.state.formData.language}
          onChange={this.handleChange('language')}
          error={this.isOnError('language')}
          helperText={this.helperText('language')}
          data-id="language"
          disabled={invalidOrganisation}
        >
          {availableLanguages.map((lang, index) => (
            <MenuItem
              key={index}
              value={lang}
            ><Translate value={`language.${lang}`}/></MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          rows={17}
          label={this.getFieldLabel('invite.customMessage', true)}
          value={this.state.formData.customMessage}
          onChange={this.handleChange('customMessage')}
          error={this.isOnError('customMessage')}
          helperText={this.helperText('')}
          disabled={invalidOrganisation}
        />

        <div className="actions col-12 inline-container space-between">
          <button
            type="button"
            className="large secondary inline-container"
            onClick={this.props.onCancel}
            data-id="cancel"
          >
            <Icon icon="go-back"/>
            <Translate value="action.back"/>
          </button>

          <button
            className="large primary inline-container"
            type="submit"
            disabled={this.state.submitting || invalidOrganisation}
          >
            <Icon icon={this.state.submitting ? 'loader' : 'paper-plane'}/>
            <Translate value="action.confirm"/>
          </button>
        </div>
      </form>
    );
  }
}

ExternalInvitationForm.defaultProps = {
  disableOrganisationFields: false
};

ExternalInvitationForm.propTypes = {
  defaultCountry: PropTypes.string.isRequired,
  title: PropTypes.node,
  onCancel: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  onValidate: PropTypes.func.isRequired,
  onInviteInternalOrganisation: PropTypes.func.isRequired,
  translateInvitationMessage: PropTypes.func.isRequired,
  currentLocale: PropTypes.string.isRequired,
  defaultValue: PropTypes.string.isRequired,
  invitingOrganisationCountry: PropTypes.string.isRequired,
  defaultOrganisation: PropTypes.object,
  disableOrganisationFields: PropTypes.bool
};

function mapStateToProps(state) {
  return {
    currentLocale: state.i18n.locale
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ExternalInvitationForm);
